import Collapse from 'bootstrap/js/dist/collapse';

import { closeAllCollapses } from '@root/src/framework/scripts/utils/bootstrap';
import { type App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Navigation Bar Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount

        .then(() => {
            mountNavigationBar(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Navigation Bar Component', error);
        });

    return function destroyNavigationBar() {
        unMountNavigationBar(listeners);

        app.logger.debug('[DXA] Navigation Bar Component destroyed');
    };
};

function mountNavigationBar(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const navigation = rootNode.querySelector<HTMLElement>('.navigation');

    if (navigation) {
        const collapses = navigation.querySelectorAll<HTMLElement>('.navigation-collapse');
        const pageLayoutContainer = rootNode.querySelector<HTMLElement>('.container');

        const menuToggleButtons = navigation.querySelectorAll<HTMLElement>('.menu-toggle button');
        const searchToggleButtons = navigation.querySelectorAll<HTMLElement>('.search-toggle button');

        const searchCollapse = navigation.querySelector<HTMLElement>('.search-collapse');
        const menuCollapse = navigation.querySelector<HTMLElement>('.menu-collapse');

        const businessUnitSelect = navigation.querySelector<HTMLElement>('.business-unit-select')
        const dropdownChevron = navigation.querySelector<HTMLElement>('.selected-input-button')

        const tabletPhoneIcon = navigation.querySelector<HTMLElement>('.tablet-phone-icon')
        const tabletPhoneBubble = navigation.querySelector<HTMLElement>('.tablet-phone-bubble')

        if (businessUnitSelect) {
            const observer = new MutationObserver(function () {
                dropdownChevron?.classList.toggle('open')
                if (businessUnitSelect?.classList.contains('show')) {
                    for (const collapse of collapses) {
                        if (collapse.classList.contains('show')) {
                            if (collapse.id == 'menu-collapse' && menuCollapse) {
                                const collapeItem = new Collapse(menuCollapse, {
                                    toggle: false,
                                });
                                collapeItem.hide();
                            }

                            if (collapse.id == 'search-collapse' && searchCollapse) {
                                const collapeItem = new Collapse(searchCollapse, {
                                    toggle: false,
                                });
                                collapeItem.hide();
                            }
                        }
                    }
                }
            })

            observer.observe(businessUnitSelect!, {
                attributes: true,
                attributeFilter: ['class'],
                childList: false,
                characterData: false
            })
        }

        if (tabletPhoneBubble && tabletPhoneIcon) {
            tabletPhoneIcon.addEventListener('click', () => {
                tabletPhoneBubble.classList.toggle('hidden');
            })

            // Checks if mouseclick is not on phone icon or bubble, then hides phone popup
            document.addEventListener('click', function (event: Event) {
                const target = event.target as Element
                if (!tabletPhoneBubble.contains(target) && !tabletPhoneIcon.contains(target)) {
                    tabletPhoneBubble.classList.add('hidden');
                }
            })
        }



        // Called on collapse toggle
        // Will add the overlay class to the page's main content div
        const toggleBackdropOpacity = () => {
            pageLayoutContainer?.classList.toggle('overlay-20');
        };

        // Called on collapse toggle
        // Will toggle between the search and xmark icons
        const toggleSearchButtons = () => {
            for (const button of searchToggleButtons) {
                button.classList.toggle('hidden');
            }
        };

        // Called on collapse toggle
        // Will toggle between the menu and xmark icons
        const toggleMenuButtons = () => {
            for (const button of menuToggleButtons) {
                button.classList.toggle('hidden');
            }
        };

        // Calls on collapse toggle
        // Will ensure the other collapse is closed when the triggered one opens
        // Calls the toggle icons function
        const toggleCollapse = (collapse: HTMLElement) => {
            if (collapse.id.includes('search')) {
                if (menuCollapse) {
                    const collapeItem = new Collapse(menuCollapse, {
                        toggle: false,
                    });
                    collapeItem.hide();
                }

                toggleSearchButtons();
            } else if (collapse.id.includes('menu')) {
                if (searchCollapse) {
                    const collapeItem = new Collapse(searchCollapse, {
                        toggle: false,
                    });
                    collapeItem.hide();
                }

                toggleMenuButtons();
            }
        };

        for (const collapse of collapses) {
            collapse.addEventListener('show.bs.collapse', () => {
                toggleCollapse(collapse);
                toggleBackdropOpacity();
            });
            listeners.push([
                collapse,
                'show.bs.collapse',
                () => {
                    toggleBackdropOpacity();
                    toggleCollapse(collapse);
                },
            ]);

            collapse.addEventListener('hidden.bs.collapse', () => {
                toggleCollapse(collapse);
                toggleBackdropOpacity();
            });
            listeners.push([
                collapse,
                'hide.bs.collapse',
                () => {
                    toggleBackdropOpacity();
                    toggleCollapse(collapse);
                },
            ]);
        }

        if (pageLayoutContainer) {
            pageLayoutContainer.addEventListener('click', () => closeAllCollapses(collapses));
            listeners.push([pageLayoutContainer, 'click', () => closeAllCollapses(collapses)]);
        }
    }
}

export function unMountNavigationBar(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}
